import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axiosInstance from '../utils/axiosInstance';
import { getImageURL } from '../utils/api';

const REMINDER_PRESETS = [
    { label: 'Tomorrow', days: 1 },
    { label: '3 days', days: 3 },
    { label: '1 week', days: 7 },
];

const MAX_RESULTS = 8;

const addDaysISO = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDate = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const digitsOnly = (value) => String(value ?? '').replace(/\D+/g, '');

const statusStyles = (key) => {
    switch (String(key ?? '').toLowerCase()) {
    case 'registered':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'in followup':
    case 'plan to register':
        return 'bg-amber-100 text-amber-700 border-amber-200';
    default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
};

const contactsOf = (row) =>
    [row.contactNumber1, row.contactNumber2, row.contactNumber3, row.contact].filter(Boolean);

const sortNewestFirst = (items) =>
    [...items].sort((a, b) => {
        const ta = new Date(a.createdDate || a.commentDate || 0).getTime() || 0;
        const tb = new Date(b.createdDate || b.commentDate || 0).getTime() || 0;
        if (tb !== ta) return tb - ta;
        return (Number(b.id) || 0) - (Number(a.id) || 0);
    });

export default function SalesStudentSearch() {
    const [rows, setRows] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [active, setActive] = useState(0);
    const [selectedId, setSelectedId] = useState(null);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [form, setForm] = useState({ comment: '', nextReminderDate: '', nextReminderComment: '', submitting: false, error: '' });
    const wrapRef = useRef(null);
    const loadingRef = useRef(false);

    const loadRows = useCallback(async () => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        try {
            const res = await axiosInstance.get('/admin/sales-inquiries');
            setRows(res?.data?.inquiries ?? []);
            setLoaded(true);
        } catch {
            // leave unloaded so the next focus retries
        } finally {
            loadingRef.current = false;
        }
    }, []);

    useEffect(() => {
        const onDocClick = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return [];
        const qDigits = digitsOnly(q);
        const isPhoneQuery = qDigits.length >= 3 && qDigits.length >= q.replace(/\s+/g, '').length - 1;

        return rows
            .filter((row) => {
                if ((row.studentName ?? '').toLowerCase().includes(q)) return true;
                if ((row.nic ?? '').toLowerCase().includes(q)) return true;
                if (isPhoneQuery) {
                    return contactsOf(row).some((c) => digitsOnly(c).includes(qDigits));
                }
                return false;
            })
            .slice(0, MAX_RESULTS);
    }, [rows, query]);

    useEffect(() => setActive(0), [query]);

    const selectedRow = selectedId ? rows.find((r) => r.id === selectedId) ?? null : null;
    const canFollowUp = selectedRow && String(selectedRow.statusKey ?? '').toLowerCase() !== 'registered';

    const loadHistory = useCallback((id) => {
        setHistoryLoading(true);
        axiosInstance
            .get(`/admin/sales-inquiries/${id}/followups`)
            .then((res) => setHistory(sortNewestFirst(res.data?.data ?? [])))
            .catch(() => setHistory([]))
            .finally(() => setHistoryLoading(false));
    }, []);

    const pick = (row) => {
        setSelectedId(row.id);
        setOpen(false);
        setMobileSearchOpen(false);
        setQuery('');
        setHistory([]);
        setForm({ comment: '', nextReminderDate: '', nextReminderComment: '', submitting: false, error: '' });
        loadHistory(row.id);
    };

    const closePanel = useCallback(() => {
        setSelectedId(null);
        setHistory([]);
    }, []);

    useEffect(() => {
        if (!selectedId) return undefined;
        const onKey = (event) => {
            if (event.key === 'Escape') closePanel();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedId, closePanel]);

    const submitFollowUp = () => {
        if (!selectedRow || form.submitting) return;
        const comment = form.comment.trim();
        if (!comment) {
            setForm((p) => ({ ...p, error: 'Comment is required.' }));
            return;
        }
        setForm((p) => ({ ...p, submitting: true, error: '' }));
        axiosInstance
            .post(`/admin/sales-inquiries/${selectedRow.id}/followups`, {
                comment,
                next_reminder_date: form.nextReminderDate || null,
                next_reminder_comment: form.nextReminderComment.trim() || null,
            })
            .then(() => {
                setForm({ comment: '', nextReminderDate: '', nextReminderComment: '', submitting: false, error: '' });
                loadHistory(selectedRow.id);
            })
            .catch((err) => {
                setForm((p) => ({
                    ...p,
                    submitting: false,
                    error: err?.response?.data?.message || err.message || 'Unable to save follow-up.',
                }));
            });
    };

    const onInputKeyDown = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((i) => Math.min(i + 1, results.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
        } else if (event.key === 'Enter' && results[active]) {
            event.preventDefault();
            pick(results[active]);
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    };

    const showDropdown = open && query.trim().length > 0;

    const resultsBody = !loaded ? (
        <p className="px-4 py-3 text-xs text-slate-500">Loading students…</p>
    ) : results.length === 0 ? (
        <p className="px-4 py-3 text-xs text-slate-500">No student found for “{query.trim()}”.</p>
    ) : (
        <ul role="listbox" className="divide-y divide-slate-100 sm:divide-y-0">
            {results.map((row, index) => (
                <li key={row.id} role="option" aria-selected={index === active}>
                    <button
                        type="button"
                        onMouseEnter={() => setActive(index)}
                        onClick={() => pick(row)}
                        className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left sm:py-2.5 ${
                            index === active ? 'bg-indigo-50' : 'bg-white'
                        }`}
                    >
                        <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold text-slate-900">
                                {row.studentName ?? '—'}
                            </span>
                            <span className="block truncate text-[11px] text-slate-500">
                                {[row.contact, row.nic, row.course].filter(Boolean).join(' · ') || '—'}
                            </span>
                        </span>
                        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusStyles(row.statusKey)}`}>
                            {row.statusLabel}
                        </span>
                    </button>
                </li>
            ))}
        </ul>
    );

    return (
        <>
            <div ref={wrapRef} className="relative hidden w-72 sm:block lg:w-96">
                <svg
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                >
                    <circle cx="11" cy="11" r="7" />
                    <path d="M20 20l-3.5-3.5" />
                </svg>
                <input
                    type="search"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => {
                        setOpen(true);
                        if (!loaded) loadRows();
                    }}
                    onKeyDown={onInputKeyDown}
                    placeholder="Search student name, NIC or mobile…"
                    aria-label="Search students"
                    className="w-full rounded-xl border border-slate-200 bg-white/80 py-2 pl-9 pr-3 text-sm text-slate-700 shadow-sm placeholder:text-slate-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                />

                {showDropdown && (
                    <div className="absolute left-0 right-0 top-full mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                        {resultsBody}
                    </div>
                )}
            </div>

            <button
                type="button"
                onClick={() => {
                    setMobileSearchOpen(true);
                    if (!loaded) loadRows();
                }}
                aria-label="Search students"
                className="rounded-xl p-2 text-slate-600 transition hover:bg-indigo-50 hover:text-indigo-700 sm:hidden"
            >
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <circle cx="11" cy="11" r="7" />
                    <path d="M20 20l-3.5-3.5" />
                </svg>
            </button>

            {mobileSearchOpen && createPortal(
                <div className="fixed inset-0 z-[55] flex flex-col bg-white sm:hidden">
                    <div className="flex h-16 shrink-0 items-center gap-2 border-b border-slate-200 px-3">
                        <div className="relative flex-1">
                            <svg
                                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            >
                                <circle cx="11" cy="11" r="7" />
                                <path d="M20 20l-3.5-3.5" />
                            </svg>
                            <input
                                autoFocus
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={onInputKeyDown}
                                placeholder="Name, NIC or mobile number"
                                aria-label="Search students"
                                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-base text-slate-800 placeholder:text-slate-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setMobileSearchOpen(false);
                                setQuery('');
                            }}
                            className="rounded-xl px-3 py-2 text-sm font-semibold text-indigo-600"
                        >
                            Cancel
                        </button>
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto">
                        {query.trim() ? (
                            resultsBody
                        ) : (
                            <p className="px-5 py-8 text-center text-sm text-slate-400">
                                Type a student name, NIC or mobile number to find them.
                            </p>
                        )}
                    </div>
                </div>,
                document.body,
            )}

            {selectedRow && createPortal(
                <div className="fixed inset-0 z-[60] flex">
                    <div className="absolute inset-0 bg-slate-900/50" onClick={closePanel} aria-hidden="true" />
                    <div
                        className="relative ml-auto flex h-full w-full max-w-lg flex-col bg-white shadow-2xl"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="student-search-title"
                    >
                        <div className="shrink-0 border-b border-slate-200 bg-slate-50 px-5 py-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <h3 id="student-search-title" className="truncate text-base font-semibold text-slate-900">
                                        {selectedRow.studentName ?? 'Student'}
                                    </h3>
                                    <span className={`mt-1 inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${statusStyles(selectedRow.statusKey)}`}>
                                        {selectedRow.statusLabel}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={closePanel}
                                    className="rounded-full p-1 text-slate-500 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400"
                                >
                                    <span className="sr-only">Close</span>
                                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none">
                                        <path d="M14.348 5.652a.75.75 0 0 0-1.06-1.06L10 7.879 6.712 4.592a.75.75 0 0 0-1.06 1.06L8.94 9l-3.287 3.288a.75.75 0 1 0 1.06 1.06L10 10.121l3.288 3.287a.75.75 0 1 0 1.06-1.06L11.061 9l3.287-3.348Z" fill="currentColor" />
                                    </svg>
                                </button>
                            </div>

                            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                                {[
                                    ['Student ID', selectedRow.studentId],
                                    ['NIC', selectedRow.nic],
                                    ['Mobile', contactsOf(selectedRow).filter((v, i, a) => a.indexOf(v) === i).join(', ')],
                                    ['Inquiry type', selectedRow.inquiryType],
                                    ['Agreed amount', selectedRow.agreedAmount != null ? `Rs ${new Intl.NumberFormat('en-US').format(Math.round(selectedRow.agreedAmount))}` : null],
                                    ['Register date', selectedRow.registerDate ? formatDate(selectedRow.registerDate) : null],
                                ].map(([label, value]) => (
                                    <div key={label} className="min-w-0">
                                        <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
                                        <dd className="truncate text-slate-800" title={value || undefined}>{value || '—'}</dd>
                                    </div>
                                ))}
                                <div className="col-span-2 min-w-0">
                                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Course</dt>
                                    <dd className="text-slate-800">{selectedRow.course || '—'}</dd>
                                </div>
                                {selectedRow.note ? (
                                    <div className="col-span-2">
                                        <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Note</dt>
                                        <dd className="whitespace-pre-line text-slate-700">{selectedRow.note}</dd>
                                    </div>
                                ) : null}
                            </dl>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Follow-up history · newest first ({history.length})
                            </h4>
                            {historyLoading ? (
                                <p className="mt-3 text-xs text-slate-500">Loading history…</p>
                            ) : history.length === 0 ? (
                                <p className="mt-3 rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">
                                    No follow-ups recorded yet.
                                </p>
                            ) : (
                                <ul className="mt-3 space-y-3">
                                    {history.map((item) => {
                                        const staffName = item.createdByStaff?.name ?? item.createdBy ?? '—';
                                        return (
                                            <li key={item.id} className="rounded-xl border border-slate-200 p-3 shadow-sm">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="flex min-w-0 items-center gap-2">
                                                        {item.createdByStaff?.imagePath ? (
                                                            <img
                                                                src={getImageURL(item.createdByStaff.imagePath)}
                                                                alt={staffName}
                                                                className="h-6 w-6 rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600">
                                                                {String(staffName).charAt(0).toUpperCase()}
                                                            </span>
                                                        )}
                                                        <span className="truncate text-[11px] font-semibold text-slate-900">{staffName}</span>
                                                    </div>
                                                    <span className="shrink-0 text-[11px] text-slate-500">
                                                        {formatDate(item.createdDate || item.commentDate)}
                                                    </span>
                                                </div>
                                                {item.comment ? (
                                                    <p className="mt-2 whitespace-pre-line text-xs text-slate-700">{item.comment}</p>
                                                ) : null}
                                                {(item.nextReminderDate || item.nextReminderComment) && (
                                                    <div className="mt-2 rounded-md bg-amber-50 px-2.5 py-2 text-[11px] text-amber-800">
                                                        <span className="font-semibold">Next: {formatDate(item.nextReminderDate)}</span>
                                                        {item.nextReminderComment ? <span> — {item.nextReminderComment}</span> : null}
                                                    </div>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>

                        {canFollowUp ? (
                            <div
                                className="shrink-0 space-y-3 border-t border-slate-200 bg-slate-50 px-5 py-4"
                                onKeyDown={(e) => {
                                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                                        e.preventDefault();
                                        submitFollowUp();
                                    }
                                }}
                            >
                                <textarea
                                    value={form.comment}
                                    onChange={(e) => setForm((p) => ({ ...p, comment: e.target.value, error: '' }))}
                                    rows={3}
                                    placeholder="Add a new follow-up…"
                                    className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                />
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Remind me</span>
                                    {REMINDER_PRESETS.map((preset) => {
                                        const iso = addDaysISO(preset.days);
                                        const isOn = form.nextReminderDate === iso;
                                        return (
                                            <button
                                                key={preset.label}
                                                type="button"
                                                onClick={() => setForm((p) => ({ ...p, nextReminderDate: isOn ? '' : iso }))}
                                                className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${
                                                    isOn
                                                        ? 'border-sky-600 bg-sky-600 text-white'
                                                        : 'border-slate-200 bg-white text-slate-600 hover:border-sky-300 hover:bg-sky-50'
                                                }`}
                                            >
                                                {preset.label}
                                            </button>
                                        );
                                    })}
                                    <input
                                        type="date"
                                        value={form.nextReminderDate}
                                        min={addDaysISO(0)}
                                        onChange={(e) => setForm((p) => ({ ...p, nextReminderDate: e.target.value }))}
                                        className="ml-auto rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                    />
                                </div>
                                {form.nextReminderDate ? (
                                    <input
                                        type="text"
                                        value={form.nextReminderComment}
                                        onChange={(e) => setForm((p) => ({ ...p, nextReminderComment: e.target.value }))}
                                        placeholder="Reminder note (optional)"
                                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                    />
                                ) : null}
                                {form.error && (
                                    <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600">{form.error}</div>
                                )}
                                <div className="flex items-center justify-between gap-3">
                                    <span className="hidden text-[11px] text-slate-400 sm:inline">Ctrl + Enter to save · Esc to close</span>
                                    <button
                                        type="button"
                                        onClick={submitFollowUp}
                                        disabled={form.submitting}
                                        className="ml-auto rounded-lg bg-sky-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-sky-400"
                                    >
                                        {form.submitting ? 'Saving...' : 'Save follow-up'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="shrink-0 border-t border-slate-200 bg-emerald-50 px-5 py-3 text-xs font-medium text-emerald-700">
                                This student is registered, so no further follow-ups are needed.
                            </div>
                        )}
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
}
